import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Lead } from '@up2stay/up2promo-api-client';
import { Card, CardContent, cn } from '@up2stay/up2promo-ui';
import { Plus } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import { LeadBoardCardContent } from '@/core/components/leads/lead-board-card-content';
import type { LeadCardMetaLines } from '@/core/components/leads/lead-card-content';
import type { LeadCardLabelMaps } from '@/core/components/leads/lead-card-labels';
import type { LeadBoardItem } from '@/core/components/leads/leads-board-item';
import { leadStatusAccent } from '@/core/components/leads/lead-status-colors';
import { resourceInteractiveCardClass } from '@/core/components/layout/list-toolbar-styles';
import { useLocale } from '@/core/providers/dictionary-provider';

type SortableLeadCardProps = {
  item: LeadBoardItem;
  lead: Lead;
  sourceLabels: Record<string, string>;
  onLeadClick: (lead: Lead) => void;
  leadCardMeta?: (lead: Lead) => LeadCardMetaLines | null | undefined;
  labelMaps?: LeadCardLabelMaps;
};

function SortableLeadCard({
  item,
  lead,
  sourceLabels,
  onLeadClick,
  leadCardMeta,
  labelMaps,
}: SortableLeadCardProps) {
  const locale = useLocale();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
    touchAction: 'pan-x pan-y' as const,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-grab select-none border-[color:var(--surface-border)] shadow-[var(--shadow-card)] active:cursor-grabbing',
        resourceInteractiveCardClass,
        isDragging && 'invisible',
      )}
      data-no-haptic="true"
      data-lead-id={item.id}
      {...attributes}
      {...listeners}
      onPointerDown={(e) => {
        if (!e.isPrimary) return;
        (
          listeners?.onPointerDown as
            | ((e: React.PointerEvent<HTMLElement>) => void)
            | undefined
        )?.(e);
      }}
    >
      <CardContent className="p-3">
        <button
          type="button"
          className="w-full min-w-0 cursor-grab select-none text-left active:cursor-grabbing"
          onClick={(e) => {
            e.stopPropagation();
            onLeadClick(lead);
          }}
        >
          <LeadBoardCardContent
            lead={lead}
            locale={locale}
            sourceLabels={sourceLabels}
            meta={leadCardMeta?.(lead) ?? undefined}
            labelMaps={labelMaps}
          />
        </button>
      </CardContent>
    </Card>
  );
}

export type LeadsBoardColumnProps = {
  columnId: string;
  columnName: string;
  data: LeadBoardItem[];
  leadsById: ReadonlyMap<string, Lead>;
  sourceLabels: Record<string, string>;
  addLeadLabel: string;
  onLeadClick: (lead: Lead) => void;
  onAddLead?: () => void;
  isDropTarget: boolean;
  lockedBoardColumnHeight?: number | null;
  leadCardMeta?: (lead: Lead) => LeadCardMetaLines | null | undefined;
  labelMaps?: LeadCardLabelMaps;
  dropHereLabel?: string;
};

export function LeadsBoardColumn({
  columnId,
  columnName,
  data,
  leadsById,
  sourceLabels,
  addLeadLabel,
  onLeadClick,
  onAddLead,
  isDropTarget,
  lockedBoardColumnHeight,
  leadCardMeta,
  labelMaps,
  dropHereLabel = 'Hier ablegen',
}: LeadsBoardColumnProps) {
  const { setNodeRef } = useDroppable({ id: columnId });
  const cardListRef = useRef<HTMLDivElement>(null);
  const [trailingCardSlotHeight, setTrailingCardSlotHeight] = useState(0);

  const filteredData = data.filter((item) => item.column === columnId);
  const items = filteredData.map((item) => item.id);
  const trailingLeadId = filteredData[filteredData.length - 1]?.id;

  useLayoutEffect(() => {
    const list = cardListRef.current;
    if (!list) return;

    const updateTrailingCardSlotHeight = () => {
      const cards = list.querySelectorAll<HTMLElement>('[data-lead-id]');
      const lastCard = cards[cards.length - 1];
      setTrailingCardSlotHeight(lastCard?.offsetHeight ?? 0);
    };

    updateTrailingCardSlotHeight();

    const resizeObserver = new ResizeObserver(updateTrailingCardSlotHeight);
    resizeObserver.observe(list);
    for (const card of list.querySelectorAll<HTMLElement>('[data-lead-id]')) {
      resizeObserver.observe(card);
    }

    return () => resizeObserver.disconnect();
  }, [items.length, trailingLeadId]);

  return (
    <div
      data-column-id={columnId}
      style={{
        ...(lockedBoardColumnHeight != null
          ? {
              height: lockedBoardColumnHeight,
              minHeight: lockedBoardColumnHeight,
            }
          : {}),
        ...(isDropTarget
          ? {
              borderStyle: 'dashed',
              borderColor: leadStatusAccent(columnId),
              borderWidth: 1.5,
            }
          : {}),
      }}
      className={cn(
        'flex min-h-[min(360px,80vh)] w-[248px] shrink-0 flex-col rounded-[var(--radius-card)] border border-[color:var(--surface-border)] bg-[color:var(--surface-2)]',
        'snap-start md:snap-align-none',
        'self-stretch',
        isDropTarget && 'bg-[color:var(--ghost-accent-bg)]',
      )}
    >
      <div
        ref={setNodeRef}
        className="flex min-h-0 flex-1 flex-col gap-2 p-2.5"
      >
        <div className="flex shrink-0 items-center gap-2 px-1 py-1 text-[13px] font-bold leading-tight text-foreground">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: leadStatusAccent(columnId) }}
            aria-hidden
          />
          <span className="truncate">{columnName}</span>
          <span className="ml-auto font-mono text-[11.5px] font-normal text-muted-foreground">
            {filteredData.length}
          </span>
        </div>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div
            ref={cardListRef}
            className="relative flex min-h-[48px] flex-1 flex-col gap-2 pb-1"
          >
            {isDropTarget && filteredData.length <= 1 ? (
              <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center px-2 text-center text-[12.5px] font-semibold text-muted-foreground">
                {dropHereLabel}
              </div>
            ) : null}
            {filteredData.map((item) => {
              const lead = leadsById.get(item.id);
              if (!lead) return null;
              return (
                <SortableLeadCard
                  key={item.id}
                  item={item}
                  lead={lead}
                  sourceLabels={sourceLabels}
                  onLeadClick={onLeadClick}
                  leadCardMeta={leadCardMeta}
                  labelMaps={labelMaps}
                />
              );
            })}
            {trailingCardSlotHeight > 0 ? (
              <div
                aria-hidden
                className="pointer-events-none shrink-0"
                style={{ height: trailingCardSlotHeight }}
              />
            ) : null}
          </div>
        </SortableContext>
        {onAddLead ? (
          <button
            type="button"
            onClick={onAddLead}
            className="inline-flex h-[34px] shrink-0 items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-[color:var(--surface-border)] text-xs text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
          >
            <Plus className="size-[13px]" aria-hidden />
            {addLeadLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
